import React, { useState, useEffect, useRef, useCallback, useContext } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import { green } from "@mui/material/colors";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import Divider from "@mui/material/Divider";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";

import ConfirmationModal from "../ConfirmationModal";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Grid from "@mui/material/Grid";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},
	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
});

const EMPTY_BLING = {
	cpf: "", cnpj: "", ie: "", email: "", celular: "", dataNascimento: "",
	logradouro: "", numero: "", complemento: "", bairro: "", municipio: "", uf: "", cep: "",
};

// dataNascimento só é obrigatório para pessoa física (CPF sem CNPJ)
const REQUIRED_FIELDS = [
	{ key: "cpf_cnpj", label: "CPF/CNPJ", check: (b) => b.cpf || b.cnpj },
	{ key: "dataNascimento", label: "Nascimento", check: (b) => b.cnpj || b.dataNascimento },
	{ key: "endereco", label: "Endereço (logradouro e CEP)", check: (b) => b.logradouro && b.cep },
];

const BlingSection = ({ contactId, contactName, contactNumber, contactEmail, blingContactIdInitial, onRegisterSave, setContactEmail }) => {
	const [blingContactId, setBlingContactId] = useState(blingContactIdInitial || null);
	const [blingData, setBlingData] = useState(EMPTY_BLING);
	// Tipo de documento (F=Pessoa Física/CPF, J=Pessoa Jurídica/CNPJ) — um campo só na UI
	const [docType, setDocType] = useState("F");
	const [loadingBling, setLoadingBling] = useState(false);
	const [savingBling, setSavingBling] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [searching, setSearching] = useState(false);
	const [showSearch, setShowSearch] = useState(false);
	const [autoMatch, setAutoMatch] = useState(null); // { id, nome } encontrado automaticamente
	const [autoLinking, setAutoLinking] = useState(false);
	const [extracting, setExtracting] = useState(false);
	const [chatExtract, setChatExtract] = useState(null); // dados extraídos do chat
	const [chatMessages, setChatMessages] = useState([]); // conversa p/ "Ver conversa"
	const [chatViewOpen, setChatViewOpen] = useState(false);
	const [blingLoadError, setBlingLoadError] = useState(false);
	const [cepLoading, setCepLoading] = useState(false);
	const searchTimer = useRef(null);
	const autoSearchDone = useRef(false);

	useEffect(() => {
		setBlingContactId(blingContactIdInitial || null);
		setAutoMatch(null);
		autoSearchDone.current = false;
	}, [blingContactIdInitial]);

	useEffect(() => {
		if (!blingContactId) {
			setBlingData({ ...EMPTY_BLING, email: contactEmail || "" });
			// Busca automática pelo número quando não há vínculo
			if (contactNumber && !autoSearchDone.current) {
				autoSearchDone.current = true;
				api.get(`/bling/contact/${contactId}/data`).then(({ data }) => {
					if (data.found && data.blingContact) {
						setAutoMatch(data.blingContact);
					}
				}).catch(() => {});
			}
			return;
		}
		fetchBlingContact(blingContactId);
	}, [blingContactId]);

	// Sincroniza o seletor PF/PJ com os dados carregados (CNPJ preenchido → PJ, senão PF)
	useEffect(() => {
		if (blingData.cnpj) setDocType("J");
		else if (blingData.cpf) setDocType("F");
	}, [blingData.cpf, blingData.cnpj]);

	const fetchBlingContact = async (bid) => {
		if (!bid) return;
		setLoadingBling(true);
		setBlingLoadError(false);
		try {
			const { data } = await api.get(`/bling/contact-detail/${bid}`);
			if (data) {
				setBlingData({
					cpf: data.cpf || "",
					cnpj: data.cnpj || "",
					ie: data.ie || "",
					email: data.email || "",
					celular: data.celular || "",
					dataNascimento: data.dataNascimento || "",
					logradouro: data.endereco?.logradouro || "",
					numero: data.endereco?.numero || "",
					complemento: data.endereco?.complemento || "",
					bairro: data.endereco?.bairro || "",
					municipio: data.endereco?.municipio || "",
					uf: data.endereco?.uf || "",
					cep: data.endereco?.cep || "",
				});
			} else {
				// Vínculo obsoleto: contato não existe mais no Bling
				setBlingLoadError(true);
				toast.warn("Este contato não existe mais no Bling. Preencha os dados e salve para recriar o vínculo automaticamente.");
			}
		} catch {
			setBlingLoadError(true);
		} finally {
			setLoadingBling(false);
		}
	};

	const blingPayload = () => {
		// Validar dataNascimento: deve ser YYYY-MM-DD e não pode ser data futura
		let dataNascimentoFinal = blingData.dataNascimento || undefined;
		if (dataNascimentoFinal) {
			const parsed = new Date(dataNascimentoFinal);
			const isValid = !isNaN(parsed.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(dataNascimentoFinal) && parsed <= new Date();
			if (!isValid) dataNascimentoFinal = undefined;
		}
		return ({
		nome: contactName || undefined,
		cpf: blingData.cpf || undefined,
		cnpj: blingData.cnpj || undefined,
		ie: blingData.ie || undefined,
		email: contactEmail || blingData.email || undefined,
		celular: contactNumber || blingData.celular || undefined,
		dataNascimento: dataNascimentoFinal,
		endereco: (blingData.logradouro || blingData.cep) ? {
			logradouro: blingData.logradouro || undefined,
			numero: blingData.numero || undefined,
			complemento: blingData.complemento || undefined,
			bairro: blingData.bairro || undefined,
			municipio: blingData.municipio || undefined,
			uf: blingData.uf || undefined,
			cep: blingData.cep || undefined,
		} : undefined,
		});
	};

	const hasBlingData = () => {
		const b = blingData;
		return !!(b.cpf || b.cnpj || b.email || b.celular || b.logradouro || b.cep || b.ie);
	};

	const handleSaveBling = useCallback(async () => {
		if (!contactId) return;
		if (!hasBlingData()) return;

		setSavingBling(true);
		try {
			let targetBlingId = blingContactId;

			// Se não vinculado, busca ou cria no Bling (sem duplicar)
			if (!targetBlingId) {
				const { data: c } = await api.get(`/contacts/${contactId}`);
				const { data: result } = await api.post("/bling/contacts/resolve", {
					contactId,
					nome: c.name,
					phone: c.number,
				});
				targetBlingId = result.blingContactId;
				if (targetBlingId) setBlingContactId(targetBlingId);
			}

			await api.patch(`/bling/contact/${contactId}/update-bling`, blingPayload());
			toast.success("Dados fiscais salvos no Bling!");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingBling(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contactId, blingContactId, blingData]);

	// Sempre registra — inclusive quando blingContactId ainda é null
	useEffect(() => {
		if (onRegisterSave) onRegisterSave(handleSaveBling);
	}, [handleSaveBling, onRegisterSave]);

	const handleSearch = (val) => {
		setSearchQuery(val);
		clearTimeout(searchTimer.current);
		if (!val || val.trim().length < 2) { setSearchResults([]); return; }
		searchTimer.current = setTimeout(async () => {
			setSearching(true);
			try {
				const { data } = await api.get("/bling/contacts/search", { params: { nome: val.trim() } });
				setSearchResults(Array.isArray(data) ? data : []);
			} catch { setSearchResults([]); }
			finally { setSearching(false); }
		}, 400);
	};

	const handleLink = async (blingId) => {
		try {
			await api.put(`/bling/contact/${contactId}/link`, { blingContactId: blingId });
			setBlingContactId(blingId);
			setShowSearch(false);
			setSearchQuery("");
			setSearchResults([]);
			setAutoMatch(null);
			toast.success("Contato vinculado ao Bling!");
			fetchBlingContact(blingId);
		} catch (err) { toastError(err); }
	};

	const handleAutoLink = async () => {
		if (!autoMatch) return;
		setAutoLinking(true);
		try {
			await api.put(`/bling/contact/${contactId}/link`, { blingContactId: autoMatch.id });
			setBlingContactId(autoMatch.id);
			setAutoMatch(null);
			toast.success(`Vinculado automaticamente: ${autoMatch.nome}`);
			fetchBlingContact(autoMatch.id);
		} catch (err) { toastError(err); }
		finally { setAutoLinking(false); }
	};

	const handleUnlink = async () => {
		if (!window.confirm("Desvincular este contato do Bling?")) return;
		try {
			await api.put(`/bling/contact/${contactId}/link`, { blingContactId: null });
			setBlingContactId(null);
			setBlingData(EMPTY_BLING);
			toast.success("Vínculo removido.");
		} catch (err) { toastError(err); }
	};

	const handleCreateInBling = async () => {
		if (!contactId) return;
		setSavingBling(true);
		try {
			const { data: c } = await api.get(`/contacts/${contactId}`);
			// Usa resolve (findOrCreate) para evitar duplicatas no Bling
			const { data: result } = await api.post("/bling/contacts/resolve", {
				contactId,
				nome: c.name,
				phone: c.number,
			});
			const newBlingId = result.blingContactId;
			if (newBlingId) setBlingContactId(newBlingId);
			// Se há dados preenchidos no formulário, envia para o Bling em seguida
			if (hasBlingData()) {
				await api.patch(`/bling/contact/${contactId}/update-bling`, blingPayload());
			}
			toast.success("Contato vinculado ao Bling com dados salvos!");
		} catch (err) { toastError(err); }
		finally { setSavingBling(false); }
	};

	const fetchCep = async (cepValue) => {
		const cep = (cepValue || "").replace(/\D/g, "");
		if (cep.length !== 8) return;
		setCepLoading(true);
		try {
			const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
			const d = await res.json();
			if (!d.erro) {
				setBlingData(prev => ({
					...prev,
					logradouro: d.logradouro || prev.logradouro || "",
					bairro: d.bairro || prev.bairro || "",
					municipio: d.localidade || prev.municipio || "",
					uf: d.uf || prev.uf || "",
				}));
			}
		} catch { /* silencioso */ }
		finally { setCepLoading(false); }
	};

	const set = (field) => (e) => {
		const value = e.target.value;
		setBlingData(d => ({ ...d, [field]: value }));
		if (field === "cep" && value.replace(/\D/g, "").length === 8) fetchCep(value);
	};

	const handleCepBlur = () => fetchCep(blingData.cep);

	const handleExtractFromChat = async () => {
		if (!contactId) return;
		setExtracting(true);
		setChatExtract(null);
		try {
			const { data } = await api.get(`/contacts/${contactId}/extract-chat-data`);
			const fields = data?.fields || {};
			const hasAny = Object.keys(fields).some(k => fields[k]?.length > 0);
			setChatExtract(hasAny ? fields : {});
			setChatMessages(Array.isArray(data?.messages) ? data.messages : []);
		} catch (err) { toastError(err); }
		finally { setExtracting(false); }
	};

	const applyExtracted = (field, rawValue) => {
		let value = rawValue;
		if (field === "dataNascimento") {
			const [d, m, y] = rawValue.split("/");
			value = `${y}-${m?.padStart(2,"0")}-${d?.padStart(2,"0")}`;
		}
		setBlingData(prev => ({ ...prev, [field]: value }));
		// e-mail extraído vai para o campo principal do contato (o "de cima"),
		// não para o antigo campo da seção Bling que foi removido
		if (field === "email") setContactEmail?.(value);
		if (field === "cep") fetchCep(value);
		setChatExtract(prev => {
			if (!prev) return prev;
			const updated = { ...prev };
			// itens agora são { value, snippet } — filtra pelo valor cru já aplicado
			if (updated[field]) updated[field] = updated[field].filter(item => item.value !== rawValue);
			return updated;
		});
	};

	const EXTRACT_LABELS = {
		cpf: "CPF", cnpj: "CNPJ", email: "E-mail", celular: "Telefone",
		cep: "CEP", dataNascimento: "Data nasc.",
	};
	const EXTRACT_FIELDS = ["cpf", "cnpj", "email", "celular", "cep", "dataNascimento"];

	// Copia um valor detectado e dá feedback.
	const copyDetected = async (value) => {
		try {
			await navigator.clipboard.writeText(value);
			toast.success(`Copiado: ${value}`);
		} catch {
			toast.error("Não foi possível copiar");
		}
	};

	// Destaca dados detectados (CPF/CNPJ/e-mail/telefone/CEP) no texto como chips
	// clicáveis que copiam ao clicar. CNPJ/CPF antes das genéricas (ordem importa).
	const DETECT_RE = /(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})|([\w.+-]+@[\w-]+\.[\w.-]+)|(\d{3}\.?\d{3}\.?\d{3}-?\d{2})|(\(?\d{2}\)?\s?9?\d{4}[-\s]?\d{4})|(\d{5}-?\d{3})/g;
	const highlightDetected = (text) => {
		if (!text) return null;
		const out = [];
		let last = 0;
		let m;
		DETECT_RE.lastIndex = 0;
		while ((m = DETECT_RE.exec(text)) !== null) {
			if (m.index > last) out.push(text.slice(last, m.index));
			const val = m[0];
			out.push(
				<span
					key={`${m.index}-${val}`}
					onClick={() => copyDetected(val.trim())}
					title="Clique para copiar"
					style={{
						background: "rgba(243,112,33,0.15)",
						color: "#C05000",
						borderRadius: 4,
						padding: "0 3px",
						cursor: "pointer",
						fontWeight: 600,
					}}
				>
					{val}
				</span>
			);
			last = m.index + val.length;
		}
		if (last < text.length) out.push(text.slice(last));
		return out;
	};

	const missingFields = REQUIRED_FIELDS.filter(f => !f.check(blingData));
	const completeFields = REQUIRED_FIELDS.filter(f => f.check(blingData));

	return (
		<>
		<Accordion defaultExpanded={Boolean(blingContactId)} style={{ marginTop: 16, boxShadow: "none", border: "1px solid #e0e0e0", borderRadius: "8px !important" }}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<img src="https://adm.graficakr.com.br/bling-icon.png" alt="Bling" style={{ height: 18, width: "auto" }} onError={e => { e.target.style.display = "none"; }} />
					<Typography variant="subtitle2" style={{ fontWeight: 700, color: "#555" }}>
						Dados Bling (Fiscal)
					</Typography>
					{blingContactId ? (
						<Chip size="small" icon={<CheckCircleIcon style={{ fontSize: 13 }} />} label="Sincronizado"
							style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", fontSize: 10, height: 20 }} />
					) : (
						<Chip size="small" icon={<WarningAmberIcon style={{ fontSize: 13 }} />} label="Não vinculado"
							style={{ backgroundColor: "#fff8e1", color: "#f57c00", border: "1px solid #ffe082", fontSize: 10, height: 20 }} />
					)}
					{blingContactId && missingFields.length > 0 && (
						<Chip size="small" icon={<WarningAmberIcon style={{ fontSize: 13 }} />}
							label={`${missingFields.length} campo(s) faltando`}
							style={{ backgroundColor: "#fff8e1", color: "#f57c00", fontSize: 10, height: 20 }} />
					)}
				</div>
			</AccordionSummary>

			<AccordionDetails style={{ paddingTop: 0 }}>
				{loadingBling && (
					<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, color: "#888", fontSize: 13 }}>
						<CircularProgress size={14} /> Carregando dados do Bling...
					</div>
				)}

				{/* Status de completude */}
				{blingContactId && !loadingBling && (
					<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
						{completeFields.map(f => (
							<Chip key={f.key} size="small" label={f.label}
								icon={<CheckCircleIcon style={{ fontSize: 12, color: "#388e3c" }} />}
								style={{ backgroundColor: "#f1f8e9", fontSize: 11, height: 22 }} />
						))}
						{missingFields.map(f => (
							<Chip key={f.key} size="small" label={f.label}
								icon={<WarningAmberIcon style={{ fontSize: 12, color: "#f57c00" }} />}
								style={{ backgroundColor: "#fff8e1", fontSize: 11, height: 22 }} />
						))}
					</div>
				)}

				{/* Vínculo */}
				{!blingContactId ? (
					<div style={{ marginBottom: 12 }}>
						{/* Sugestão automática por telefone */}
						{autoMatch && (
							<div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 6, marginBottom: 10 }}>
								<CheckCircleIcon style={{ color: "#388e3c", fontSize: 18, flexShrink: 0 }} />
								<div style={{ flex: 1, fontSize: 13 }}>
									<strong>Encontrado no Bling:</strong> {autoMatch.nome}
									<div style={{ fontSize: 11, color: "#555" }}>ID {autoMatch.id} — encontrado pelo número de telefone</div>
								</div>
								<Button size="small" variant="contained"
									disabled={autoLinking}
									onClick={handleAutoLink}
									startIcon={autoLinking ? <CircularProgress size={12} color="inherit" /> : <LinkIcon />}
									style={{ backgroundColor: "#388e3c", color: "#fff", fontSize: 11, flexShrink: 0 }}>
									Vincular
								</Button>
								<IconButton size="small" onClick={() => setAutoMatch(null)} style={{ flexShrink: 0 }}>
									<WarningAmberIcon fontSize="small" style={{ color: "#aaa" }} />
								</IconButton>
							</div>
						)}
						<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
							<Button size="small" variant="outlined" startIcon={<SearchIcon />}
								onClick={() => setShowSearch(v => !v)}
								style={{ borderColor: "#F37021", color: "#F37021", fontSize: 12 }}>
								Buscar no Bling
							</Button>
							<Button size="small" variant="outlined" startIcon={<AddIcon />}
								onClick={handleCreateInBling} disabled={savingBling}
								style={{ borderColor: "#1565c0", color: "#1565c0", fontSize: 12 }}>
								{savingBling ? <CircularProgress size={12} /> : "Criar no Bling"}
							</Button>
						</div>
						{showSearch && (
							<div>
								<TextField
									size="small" fullWidth
									placeholder="Buscar por nome no Bling..."
									value={searchQuery}
									onChange={e => handleSearch(e.target.value)}
									InputProps={{
										startAdornment: searching
											? <CircularProgress size={14} style={{ marginRight: 6 }} />
											: <SearchIcon fontSize="small" style={{ color: "#aaa", marginRight: 4 }} />,
									}}
									style={{ marginBottom: 6 }}
								/>
								{searchResults.length > 0 && (
									<div style={{ border: "1px solid #ddd", borderRadius: 6, overflow: "hidden", maxHeight: 200, overflowY: "auto" }}>
										{searchResults.map(r => (
											<div key={r.id} style={{ padding: "6px 12px", borderBottom: "1px solid #f5f5f5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
												<div>
													<div style={{ fontWeight: 600, fontSize: 13 }}>{r.nome}</div>
													{r.numeroDocumento && <div style={{ fontSize: 11, color: "#555" }}>{r.numeroDocumento.replace(/\D/g, "").length > 11 ? "CNPJ" : "CPF"}: {r.numeroDocumento}</div>}
													{(r.celular || r.telefone) && <div style={{ fontSize: 11, color: "#888" }}>{r.celular || r.telefone}</div>}
												</div>
												<Button size="small" variant="contained" onClick={() => handleLink(r.id)}
													style={{ backgroundColor: "#F37021", color: "#fff", fontSize: 11, padding: "2px 10px" }}>
													Vincular
												</Button>
											</div>
										))}
									</div>
								)}
								{searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
									<Typography variant="caption" color="textSecondary">Nenhum contato encontrado no Bling.</Typography>
								)}
							</div>
						)}
					</div>
				) : (
					<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
						<Button size="small" variant="text" startIcon={<LinkOffIcon style={{ fontSize: 14 }} />}
							onClick={handleUnlink}
							style={{ color: "#999", fontSize: 11 }}>
							Desvincular
						</Button>
					</div>
				)}

				{/* Campos Bling — sempre visíveis para preenchimento */}
				<Grid container spacing={1}>
					<Grid item xs={12} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
						<Button
							size="small"
							variant="outlined"
							startIcon={extracting ? <CircularProgress size={14} /> : <FindInPageIcon style={{ fontSize: 16 }} />}
							onClick={handleExtractFromChat}
							disabled={extracting}
							style={{ fontSize: 11, borderColor: "#1565c0", color: "#1565c0" }}
						>
							{extracting ? "Buscando..." : "Buscar dados no chat"}
						</Button>
						{chatMessages.length > 0 && (
							<Button
								size="small"
								variant="text"
								onClick={() => setChatViewOpen(true)}
								style={{ fontSize: 11, color: "#555" }}
							>
								Ver conversa ({chatMessages.length})
							</Button>
						)}
					</Grid>

					{/* Estado de carregamento / erro do vínculo Bling */}
					{blingContactId && loadingBling && (
						<Grid item xs={12}>
							<div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#666" }}>
								<CircularProgress size={12} /> Carregando dados do Bling...
							</div>
						</Grid>
					)}
					{blingContactId && blingLoadError && (
						<Grid item xs={12}>
							<div style={{ fontSize: 12, color: "#c62828", background: "#ffebee", border: "1px solid #ef9a9a", borderRadius: 4, padding: "6px 10px" }}>
								Não foi possível carregar os dados do Bling. O contato pode ter sido removido ou o token expirou.
							</div>
						</Grid>
					)}
					{/* Resultados extraídos do chat */}
					{chatExtract !== null && (
						<Grid item xs={12}>
							{Object.keys(chatExtract).filter(k => chatExtract[k]?.length > 0).length === 0 ? (
								<div style={{ fontSize: 12, color: "#888", padding: "4px 0" }}>
									Nenhum dado estruturado encontrado nas mensagens.
								</div>
							) : (
								<div style={{ background: "#e8f0fe", border: "1px solid #90caf9", borderRadius: 6, padding: "8px 10px" }}>
									<div style={{ fontSize: 11, fontWeight: 700, color: "#1565c0", marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>
										Dados encontrados no chat — clique para aplicar:
									</div>
									<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
										{EXTRACT_FIELDS.filter(f => chatExtract[f]?.length > 0).map(field =>
											chatExtract[field].map((item, i) => (
												<Chip
													key={`${field}-${i}`}
													size="small"
													label={`${EXTRACT_LABELS[field]}: ${item.value}`}
													onClick={() => applyExtracted(field, item.value)}
													style={{ fontSize: 11, cursor: "pointer", background: "#fff", border: "1px solid #1565c0", color: "#1565c0" }}
												/>
											))
										)}
									</div>
								</div>
							)}
						</Grid>
					)}

					<Grid item xs={12}>
						<Typography variant="caption" style={{ fontWeight: 700, color: "#777", textTransform: "uppercase", letterSpacing: .5 }}>
							Documento
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
							<Chip label="Pessoa Física" size="small" clickable
								color={docType === "F" ? "primary" : "default"}
								variant={docType === "F" ? "filled" : "outlined"}
								onClick={() => { setDocType("F"); setBlingData(d => ({ ...d, cnpj: "" })); }} />
							<Chip label="Pessoa Jurídica" size="small" clickable
								color={docType === "J" ? "primary" : "default"}
								variant={docType === "J" ? "filled" : "outlined"}
								onClick={() => { setDocType("J"); setBlingData(d => ({ ...d, cpf: "" })); }} />
						</div>
						<TextField size="small" fullWidth
							label={docType === "J" ? "CNPJ" : "CPF"}
							placeholder={docType === "J" ? "00.000.000/0000-00" : "000.000.000-00"}
							value={docType === "J" ? blingData.cnpj : blingData.cpf}
							onChange={set(docType === "J" ? "cnpj" : "cpf")}
							variant="outlined" />
					</Grid>
					<Grid item xs={12}>
						<TextField size="small" fullWidth label="Inscrição Estadual (opcional)" value={blingData.ie} onChange={set("ie")}
							placeholder="Ex: 123.456.789.0001" variant="outlined" />
					</Grid>

					<Grid item xs={12} style={{ marginTop: 8 }}>
						<Typography variant="caption" style={{ color: "#999", fontStyle: "italic" }}>
							📞 Telefone e e-mail vêm do cadastro do contato — sem duplicar aqui.
						</Typography>
					</Grid>
					<Grid item xs={6}>
						<TextField size="small" fullWidth label="Data de nascimento" value={blingData.dataNascimento}
							onChange={set("dataNascimento")} type="date" InputLabelProps={{ shrink: true }} variant="outlined" />
					</Grid>

					<Grid item xs={12} style={{ marginTop: 8 }}>
						<Typography variant="caption" style={{ fontWeight: 700, color: "#777", textTransform: "uppercase", letterSpacing: .5 }}>
							Endereço
						</Typography>
					</Grid>
					<Grid item xs={4}>
						<TextField size="small" fullWidth label="CEP" value={blingData.cep} onChange={set("cep")}
							onBlur={handleCepBlur} placeholder="30150-312" variant="outlined"
							InputProps={cepLoading ? { endAdornment: <CircularProgress size={12}/> } : {}}
							helperText={cepLoading ? "Buscando endereço..." : "Preencha o CEP para auto-preencher"} />
					</Grid>
					<Grid item xs={8}>
						<TextField size="small" fullWidth label="Logradouro (rua/avenida)" value={blingData.logradouro} onChange={set("logradouro")}
							placeholder="Rua, Av., etc." variant="outlined" />
					</Grid>
					<Grid item xs={3}>
						<TextField size="small" fullWidth label="Número" value={blingData.numero} onChange={set("numero")}
							variant="outlined" />
					</Grid>
					<Grid item xs={9}>
						<TextField size="small" fullWidth label="Complemento" value={blingData.complemento} onChange={set("complemento")}
							placeholder="Apto, sala..." variant="outlined" />
					</Grid>
					<Grid item xs={6}>
						<TextField size="small" fullWidth label="Bairro" value={blingData.bairro} onChange={set("bairro")}
							variant="outlined" />
					</Grid>
					<Grid item xs={4}>
						<TextField size="small" fullWidth label="Cidade" value={blingData.municipio} onChange={set("municipio")}
							variant="outlined" />
					</Grid>
					<Grid item xs={2}>
						<TextField size="small" fullWidth label="UF" value={blingData.uf} onChange={set("uf")}
							placeholder="MG" inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }} variant="outlined" />
					</Grid>

					<Grid item xs={12} style={{ marginTop: 10, borderTop: "1px solid #f0f0f0", paddingTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
						{blingContactId ? (
							<>
								<Button
									variant="outlined"
									size="small"
									onClick={handleSaveBling}
									disabled={savingBling}
									startIcon={savingBling ? <CircularProgress size={14} /> : <LinkIcon style={{ fontSize: 14 }} />}
									style={{ borderColor: "#1565c0", color: "#1565c0", fontSize: 11 }}
								>
									{savingBling ? "Salvando..." : "Salvar só no Bling"}
								</Button>
								<Button
									variant="outlined"
									size="small"
									startIcon={<ContentCopyIcon style={{ fontSize: 14 }} />}
									onClick={() => {
										const lines = [];
										if (contactName) lines.push(`*Nome Completo:* ${contactName}`);
										if (blingData.email) lines.push(`*E-mail:* ${blingData.email}`);
										if (blingData.cpf) lines.push(`*CPF:* ${blingData.cpf}`);
										if (blingData.cnpj) lines.push(`*CNPJ:* ${blingData.cnpj}`);
										if (blingData.ie) lines.push(`*Inscrição Estadual:* ${blingData.ie}`);
										if (blingData.celular) lines.push(`*Telefone:* ${blingData.celular}`);
										const addrParts = [
											blingData.logradouro,
											blingData.numero ? `, ${blingData.numero}` : "",
											blingData.complemento ? ` - ${blingData.complemento}` : "",
											blingData.bairro ? ` - ${blingData.bairro}` : "",
											blingData.municipio ? ` - ${blingData.municipio}` : "",
											blingData.uf ? `/${blingData.uf}` : "",
											blingData.cep ? ` - CEP ${blingData.cep}` : "",
										];
										const addr = addrParts.filter(Boolean).join("").trim();
										if (addr) lines.push(`*Endereço:* ${addr}`);
										if (lines.length === 0) { toast.warn("Nenhum dado preenchido para copiar."); return; }
										navigator.clipboard.writeText(lines.join("\n"));
										toast.success("Dados copiados!");
									}}
									style={{ borderColor: "#555", color: "#555", fontSize: 11 }}
								>
									Copiar dados
								</Button>
								<Button
									variant="text"
									size="small"
									component="a"
									href={`https://www.bling.com.br/contatos.php#edit/${blingContactId}`}
									target="_blank"
									rel="noreferrer"
									style={{ fontSize: 11, color: "#888" }}
								>
									Abrir no Bling ↗
								</Button>
							</>
						) : (
							<Typography variant="caption" style={{ color: "#aaa", fontStyle: "italic" }}>
								Vincule um contato Bling para habilitar o salvamento fiscal
							</Typography>
						)}
					</Grid>
				</Grid>
			</AccordionDetails>
		</Accordion>

		{/* Diálogo "Ver conversa" — conversa recente (ambos os lados) para copiar/colar
		    manualmente o que a extração automática não pegou. */}
		<Dialog open={chatViewOpen} onClose={() => setChatViewOpen(false)} maxWidth="sm" fullWidth>
			<DialogTitle style={{ fontSize: 14, paddingBottom: 4 }}>
				Conversa — {contactName}
			</DialogTitle>
			<DialogContent dividers style={{ padding: "8px 12px", maxHeight: "60vh" }}>
				{chatMessages.length === 0 ? (
					<Typography variant="body2" style={{ color: "#888" }}>Sem mensagens.</Typography>
				) : (
					<>
						<Typography variant="caption" style={{ color: "#888", display: "block", marginBottom: 6 }}>
							Dados detectados aparecem destacados — clique para copiar.
						</Typography>
						<div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
							{chatMessages.map((m, i) => (
								<div key={i} style={{
									alignSelf: m.fromMe ? "flex-end" : "flex-start",
									maxWidth: "88%",
									background: m.fromMe ? "#dcf8c6" : "#f0f0f0",
									borderRadius: 8, padding: "7px 10px",
								}}>
									<Typography variant="body2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#222", lineHeight: 1.4 }}>
										{highlightDetected(m.body)}
									</Typography>
								</div>
							))}
						</div>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button size="small" onClick={() => setChatViewOpen(false)}>Fechar</Button>
			</DialogActions>
		</Dialog>
		</>
	);
};

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMounted = useRef(true);
	const blingSaveRef = useRef(null);
	const { user } = useContext(AuthContext);
	// LGPD/Fase 2.2: dados fiscais Bling (CPF/CNPJ/endereço) só para admin/supervisor.
	const canEditFiscal = ["admin", "supervisor"].includes(user?.profile);

	const initialState = { name: "", number: "", email: "" };
	const [contact, setContact] = useState(initialState);
	const [contactTags, setContactTags] = useState([]);
	const [allTags, setAllTags] = useState([]);
	const [showTagPicker, setShowTagPicker] = useState(false);
	const [blingContactId, setBlingContactId] = useState(null);
	const [pendingSave, setPendingSave] = useState(null);
	const [confirmNumberChange, setConfirmNumberChange] = useState(false);

	useEffect(() => {
		return () => { isMounted.current = false; };
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => ({ ...prevState, ...initialValues }));
			}
			if (!contactId) return;
			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact(data);
					setBlingContactId(data.blingContactId || null);
				}
			} catch (err) { toastError(err); }
		};
		fetchContact();
	}, [contactId, open, initialValues]);

	useEffect(() => {
		if (!contactId || !open) return;
		api.get(`/contacts/${contactId}/tags`)
			.then(({ data }) => setContactTags(Array.isArray(data) ? data : []))
			.catch(() => {});
	}, [contactId, open]);

	useEffect(() => {
		if (!open) return;
		api.get("/tags/all")
			.then(({ data }) => setAllTags(Array.isArray(data) ? data : []))
			.catch(() => {});
	}, [open]);

	const handleAddTag = async (tag) => {
		setShowTagPicker(false);
		if (!contactId) return;
		try {
			await api.post(`/contacts/${contactId}/tags`, { name: tag.name, color: tag.color });
			const { data } = await api.get(`/contacts/${contactId}/tags`);
			setContactTags(Array.isArray(data) ? data : []);
		} catch (err) { toastError(err); }
	};

	const handleRemoveTag = async (tagId) => {
		try {
			await api.delete(`/tags/${tagId}`);
			setContactTags(prev => prev.filter(t => t.id !== tagId));
		} catch (err) { toastError(err); }
	};

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setContactTags([]);
		setShowTagPicker(false);
		setBlingContactId(null);
		setPendingSave(null);
		setConfirmNumberChange(false);
	};

	const handleSaveContact = async values => {
		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, values);
			} else {
				const { data } = await api.post("/contacts", values);
				if (onSave) onSave(data);
			}
			// Salva dados Bling automaticamente junto com o contato
			if (blingSaveRef.current) {
				await blingSaveRef.current();
			}
			toast.success(i18n.t("contactModal.success"));
			handleClose();
		} catch (err) { toastError(err); }
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							// Trocar o número WhatsApp de um contato existente quebra o vínculo
							// contato↔WhatsApp↔tickets — exige confirmação explícita antes de salvar.
							if (contactId && contact.number && values.number !== contact.number) {
								setPendingSave(values);
								setConfirmNumberChange(true);
							} else {
								handleSaveContact(values);
							}
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								{/* ── DADOS CHATKR ── */}
								<Typography variant="subtitle2" style={{ fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
									Dados ChatKR
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.number")}
									name="number"
									error={touched.number && Boolean(errors.number)}
									helperText={touched.number && errors.number}
									placeholder="5513912344321"
									variant="outlined"
									margin="dense"
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>

								<Typography variant="subtitle2"
									style={{ fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: .5, marginTop: 12, marginBottom: 8 }}>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>
								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo && values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div className={classes.extraAttr} key={`${index}-info`}>
														<Field as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined" margin="dense" className={classes.textField} />
														<Field as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined" margin="dense" className={classes.textField} />
														<IconButton size="small" onClick={() => remove(index)}>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button style={{ flex: 1, marginTop: 8 }} variant="outlined" color="primary"
													onClick={() => push({ name: "", value: "" })}>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>

								{/* Tags */}
								{contactId && (
									<>
										<Typography variant="subtitle2" style={{ marginTop: 12, marginBottom: 6, display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: .5, fontSize: 11 }}>
											<LocalOfferIcon style={{ fontSize: 14 }} /> Profissão / Tags
										</Typography>
										<div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
											{contactTags.map(tag => (
												<Chip key={tag.id} label={tag.name} size="small"
													onDelete={() => handleRemoveTag(tag.id)}
													style={{ backgroundColor: tag.color, color: "#fff", fontWeight: 600, fontSize: 11 }} />
											))}
											<Chip icon={<AddIcon style={{ fontSize: 13 }} />} label="Adicionar" size="small"
												onClick={() => setShowTagPicker(v => !v)}
												style={{ border: "1px dashed #ccc", background: "transparent", color: "#888", cursor: "pointer" }} />
										</div>
										{showTagPicker && (
											<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, padding: 10, background: theme.palette.background.default, borderRadius: 6 }}>
												{allTags.filter(t => !contactTags.some(ct => ct.name === t.name)).map(tag => (
													<Chip key={tag.name} label={tag.name} size="small" onClick={() => handleAddTag(tag)}
														style={{ backgroundColor: tag.color, color: "#fff", fontWeight: 600, fontSize: 11, cursor: "pointer" }} />
												))}
												{allTags.filter(t => !contactTags.some(ct => ct.name === t.name)).length === 0 && (
													<span style={{ fontSize: 12, color: "#999" }}>Todas as tags já aplicadas</span>
												)}
											</div>
										)}
									</>
								)}

								{/* ── DADOS BLING ── (LGPD/Fase 2.2: só admin/supervisor) */}
								{contactId && canEditFiscal && (
									<BlingSection
										contactId={contactId}
										contactName={values.name}
										contactNumber={contact.number}
										contactEmail={values.email}
										blingContactIdInitial={blingContactId}
										onRegisterSave={fn => { blingSaveRef.current = fn; }}
										setContactEmail={v => setFieldValue("email", v)}
									/>
								)}
							</DialogContent>
							<DialogActions>
								<Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={classes.btnWrapper}>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
			<ConfirmationModal
				title="Confirmar troca de número do WhatsApp"
				open={confirmNumberChange}
				onClose={() => { setConfirmNumberChange(false); setPendingSave(null); }}
				onConfirm={() => {
					if (pendingSave) handleSaveContact(pendingSave);
					setPendingSave(null);
				}}
			>
				Você está prestes a trocar o número do WhatsApp deste contato de{" "}
				<strong>{contact.number}</strong> para <strong>{pendingSave?.number}</strong>.
				Isso pode quebrar o vínculo deste contato com o histórico de tickets e
				mensagens já existentes. Tem certeza que quer continuar?
			</ConfirmationModal>
		</div>
	);
};

export default ContactModal;
